import { useQuery } from '@tanstack/react-query'
import { Address } from 'viem'
import { useAccount } from 'wagmi'

export interface RegisteredCollection {
  name: string
  main: Address
  shadow: Address
}

const ETHERSCAN_API = 'https://api.etherscan.io/v2/api'
const REGISTRY_ADDRESS = '0xF781583039fB1B0A08D7ececEFf53fE2a5908858'
const SHADOW_REGISTERED_TOPIC = '0x54b5914cf49a73a7bb45583bcfb9d71d3cfd6e7d8028a2c96eaf0c9faa4722fd'

export function useFPTRegistry() {
  const { address } = useAccount()

  const getRegisteredCols = async (): Promise<RegisteredCollection[]> => {
    try {
      const apiKey = process.env.NEXT_PUBLIC_ETHERSCAN_API_KEY || ''
      const url = `${ETHERSCAN_API}?chainid=1&module=logs&action=getLogs&address=${REGISTRY_ADDRESS}&topic0=${SHADOW_REGISTERED_TOPIC}&fromBlock=17000000&toBlock=latest&apikey=${apiKey}`
      const response = await fetch(url)
      const data = await response.json()

      if (data.status !== '1' || !data.result) {
        console.log('ETHERSCAN API ERROR', data)
        return []
      }

      return data.result.map((log: { topics: string[]; data: string }): RegisteredCollection => {
        // topics[1] = principal (indexed), topics[2] = surrogate (indexed)
        const principal = ('0x' + log.topics[1].slice(26)) as Address
        const surrogate = ('0x' + log.topics[2].slice(26)) as Address

        // Decode name from data (skip first 64 chars for offset, next 64 for length, then read string)
        const dataWithoutPrefix = log.data.slice(2)
        const nameOffset = parseInt(dataWithoutPrefix.slice(0, 64), 16) * 2
        const nameLength = parseInt(dataWithoutPrefix.slice(nameOffset, nameOffset + 64), 16)
        const nameHex = dataWithoutPrefix.slice(nameOffset + 64, nameOffset + 64 + nameLength * 2)
        const name = Buffer.from(nameHex, 'hex').toString('utf8')

        return {
          name,
          main: principal,
          shadow: surrogate,
        }
      })
    } catch (error) {
      console.log('SHADOW REGISTERED ERROR', error)
      return []
    }
  }

  return useQuery({
    queryKey: [
      {
        entity: 'fptRegistry',
      },
    ],
    queryFn: async () => await getRegisteredCols(),
    enabled: !!address,
  })
}
